"use client"

import { Button } from "@/components/ui/button"
import { ArrowRight, Menu, X } from "lucide-react"
import { Link } from "react-router-dom"
import { useState, useEffect } from "react"
import { AnimatedLogo } from "./animated-logo"

export function ClassyHeader() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const navItems = [
    { name: "Features", href: "#features" },
    { name: "Pricing", href: "#pricing" },
    { name: "About", href: "#about" },
    { name: "Contact", href: "#contact" },
  ]

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-gray-200/50 shadow-sm"
          : "bg-white/80 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="container flex h-16 max-w-screen-2xl items-center justify-between">
        <Link to="/" className="flex-shrink-0">
          <AnimatedLogo />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          {navItems.map((item, index) => (
            <Link
              key={item.name}
              to={item.href}
              className="relative text-gray-600 hover:text-gray-900 transition-colors duration-200 font-medium group"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {item.name}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 transition-all duration-300 group-hover:w-full"></span>
            </Link>
          ))}
        </nav>

        {/* Desktop CTA Buttons */}
        <div className="hidden md:flex items-center space-x-3">
          <Link to="/auth/login">
            <Button
              variant="ghost"
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all duration-200"
            >
              Sign In
            </Button>
          </Link>
          <Link to="/auth/login">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md transition-all duration-200 group">
              Request Demo
              <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Button>
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <Button variant="ghost" size="sm" className="md:hidden" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Mobile Navigation */}
      <div
        className={`md:hidden transition-all duration-300 ease-in-out ${
          isMobileMenuOpen
            ? "max-h-64 opacity-100 border-b border-gray-200"
            : "max-h-0 opacity-0 border-b border-transparent"
        } overflow-hidden bg-white/95 backdrop-blur-xl`}
      >
        <nav className="container py-4 space-y-4">
          {navItems.map((item, index) => (
            <Link
              key={item.name}
              to={item.href}
              className="block text-gray-600 hover:text-gray-900 transition-colors duration-200 font-medium py-2"
              onClick={() => setIsMobileMenuOpen(false)}
              style={{
                animationDelay: `${index * 50}ms`,
                animation: isMobileMenuOpen ? "slideInFromTop 0.3s ease-out forwards" : "none",
              }}
            >
              {item.name}
            </Link>
          ))}
          <div className="flex flex-col space-y-2 pt-4 border-t border-gray-200">
            <Link to="/auth/login" onClick={() => setIsMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-gray-600 hover:text-gray-900">
                Sign In
              </Button>
            </Link>
            <Link to="/auth/login" onClick={() => setIsMobileMenuOpen(false)}>
              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                Request Demo
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </nav>
      </div>

      <style>{`
        @keyframes slideInFromTop {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </header>
  )
}
